import {assert} from 'Asserts';
import {PipelineEdge, PipelineStep, PipelineStepData, PipelineStepRawData} from 'pipelines/PipelineTypes';

import {AnalysisGraphEdgeData} from './AnalysisGraphTypes';

function getNodeId({id}: Pick<PipelineStep, 'id'>): string {
  return `node-${id}`;
}

function getHandleId({name}: Pick<PipelineStepData, 'name'>): string {
  return `handle-${name}`;
}

function getEdgeId(edge: PipelineEdge): string {
  return `edge-${edge.fromStep.id}-${edge.toStep.id}-${edge.fromStepData.name}-${edge.toStepData.name}`;
}

/**
 * Get edge data for the `AnalysisGraph`.
 *
 * This translates the ID-only interfaces of `PipelineEdge` to the full interfaces of `AnalysisGraphEdgeData`.
 */
function getEdgeData(steps: Array<PipelineStep>, edge: PipelineEdge): AnalysisGraphEdgeData {
  const fromStep = steps.find(({id}) => id === edge.fromStep.id);
  const toStep = steps.find(({id}) => id === edge.toStep.id);

  assert(fromStep != null && toStep != null, 'Edge references step that does not exist.');

  const fromStepData = fromStep.outputStepDatas.find(({name}) => name === edge.fromStepData.name);
  const toStepData = toStep.inputStepDatas.find(({name}) => name === edge.toStepData.name);

  assert(fromStepData != null && toStepData != null, 'Edge references step data that does not exist.');
  return {edge: {...edge, fromStep, fromStepData, toStep, toStepData}};
}

/**
 * Get raw datas that travel along an edge.
 */
function getEdgeRawDatas(
  edge: Pick<
    AnalysisGraphEdgeData['edge'],
    'fromStepData' | 'fromStepDataIndex' | 'toStepData' | 'toStepDataIndex'
  >
): Array<PipelineStepRawData> {
  const toStepRawDatas =
    edge.toStepDataIndex == null
      ? edge.toStepData.rawDatas
      : edge.toStepData.rawDatas.slice(edge.toStepDataIndex, edge.toStepDataIndex + 1);

  const fromStepRawDatas =
    edge.fromStepDataIndex == null
      ? edge.fromStepData.rawDatas
      : edge.fromStepData.rawDatas.slice(edge.fromStepDataIndex, edge.fromStepDataIndex + 1);

  const toStepRawDataIds = new Set<string>(toStepRawDatas.map(({id}) => id));

  return fromStepRawDatas.filter(({id}) => toStepRawDataIds.has(id));
}

export default {getEdgeId, getNodeId, getHandleId, getEdgeData, getEdgeRawDatas};
